import Refluxr from '../../lib/refluxr';
import DataTargetActions from '../actions/datatarget.actions';

export const verificationMessage = (code, title) => ({
   Accessible: `Spreadsheet accessible${title ? `: ${title}` : ''}.`,
   AccessDenied: 'Access denied. Share this spreadsheet with the configured Google service account.',
   NotFoundOrInaccessible: 'Spreadsheet could not be found or accessed. Check its ID and sharing permissions.',
   GoogleAuthenticationFailed: 'Google authentication failed. Contact an administrator.',
   GoogleConfigurationInvalid: 'Google service account configuration is invalid.',
   GoogleConfigurationMissing: 'Google service account is not configured.',
   ProviderError: 'Google Sheets could not be reached. Try again.',
   TargetArchived: 'This Target is deleted. Restore it before verifying access.',
   NotFound: 'This Target was not found.',
   InvalidTargetId: 'The selected Target is invalid. Refresh the list and try again.',
   UnsupportedTargetType: 'This Target type is not supported. Please contact OpsPerfect support.'
})[code] || 'Verification failed. Please try again.';


const initialState = {
   dataTargets: [], targetsLoading: false, targetsError: null, targetFilter: 'Active', lifecycleBusy: false, lifecycleError: null, lifecycleMessage: null, targetEditBusy: false, targetEditError: null, deletedDuplicate: null,
   targetDetail: null, targetDetailLoading: false, targetDetailError: null,
   targetSaving: false, targetSaveMessage: null, targetSaveError: null, targetRegisteredId: null,
   selectedTargetId: null, targetVerifying: false, targetVerifyMessage: null, targetVerifyError: null
};

class DataTargetStore extends Refluxr.Store {
   constructor() {
      super();
      this.state = initialState;
      this.listenables = [DataTargetActions];
      this.listRequest = 0;
      this.detailRequest = 0;
      this.verifyRequest = 0;
      this.mutationRequest = 0;
      this.registrationRequest = 0;
   }

   onFetchTargets(input = {}) {
      if (!input.requestId) input.requestId = ++this.listRequest;
      if (input.requestId !== this.listRequest) return; // A retry of an older request.
      this.setState({ targetsLoading: true, targetsError: null });
   }

   onFetchTargetsCompleted({ input, data }) {
      if (input.requestId !== this.listRequest) return;
      const result = data && data.content || {};
      this.setState({ dataTargets: result.targets || [], targetsLoading: false });
   }

   onFetchTargetsFailed({ input }) {
      if (input.requestId !== this.listRequest) return;
      this.setState({ targetsLoading: false, targetsError: 'Could not load Targets. Please retry.' });
   }

   onRegisterTarget(input = {}) {
      input.requestId = ++this.registrationRequest;
      this.setState({ targetSaving: true, targetSaveError: null, targetSaveMessage: null, targetRegisteredId: null });
   }

   onRegisterTargetCompleted({ input, data }) {
      if (input && input.requestId && input.requestId !== this.registrationRequest) return;
      const result = data && data.content || {};
      this.setState({ targetSaving: false, targetSaveMessage: 'Target registered successfully.', targetRegisteredId: result.target.id, deletedDuplicate: null });
      DataTargetActions.fetchTargets({});
   }

   onRegisterTargetFailed({ input, data }) {
      if (input && input.requestId && input.requestId !== this.registrationRequest) return;
      const result = data && (data.content || data) || {};
      const code = result.code;
      const existing = code === 'AlreadyExists' && result.target;
      this.setState({
         targetSaving: false,
         targetSaveError: code === 'AlreadyExists' ? 'This spreadsheet is already registered for this company.' :
            code === 'InvalidSpreadsheetId' ? 'Enter a valid Google Spreadsheet ID.' :
            code === 'InvalidTargetName' ? 'Enter a valid Target name.' :
            code === 'UnsupportedTargetType' ? 'This Target type is not supported.' : 'Could not register Target. Please retry.',
         targetRegisteredId: existing && !existing.archivedAt && existing.id ? existing.id : null,
         deletedDuplicate: existing && existing.archivedAt ? existing : null,
         targetSaveMessage: null
      });
      if (existing && existing.id) DataTargetActions.fetchTargets({});
   }

   onSetTargetFilter(filter) {
      if (!['Active', 'Deleted'].includes(filter)) return;
      this.onClearSelectedTarget();
      this.setState({ targetFilter: filter, lifecycleError: null, lifecycleMessage: null });
   }

   onViewDeletedTarget() {
      const target = this.state.deletedDuplicate;
      if (!target) return;
      this.onSetTargetFilter('Deleted');
      this.setState({ deletedDuplicate: null, targetSaveError: null });
      DataTargetActions.getTarget({ id: target.id });
   }

   beginMutation(input, mode) {
      // Mutation ownership is independent of whichever detail panel is open.
      // The UI prevents a second command while this one is pending.
      if (this.pendingMutation) return;
      input.requestId = ++this.mutationRequest;
      this.pendingMutation = { id: input.id, mode, requestId: input.requestId };
      ++this.verifyRequest;
      ++this.detailRequest;
      this.setState({ lifecycleBusy: true, targetEditBusy: mode === 'update', lifecycleError: null,
         lifecycleMessage: null, targetEditError: null, targetVerifying: false,
         targetVerifyMessage: null, targetVerifyError: null, targetDetailLoading: false });
   }

   finishMutation(input, mode) {
      const pending = this.pendingMutation;
      if (!pending || pending.mode !== mode || pending.id !== input.id || pending.requestId !== input.requestId) return false;
      this.pendingMutation = null;
      return true;
   }

   completeMutation({ input, data }, mode) {
      if (!this.finishMutation(input, mode)) return;
      const target = data && (data.content || data) && (data.content || data).target;
      if (!target) {
         this.setState({ lifecycleBusy: false, targetEditBusy: false, lifecycleError: 'Could not reconcile Target update. Refresh and retry.' });
         DataTargetActions.fetchTargets({});
         return;
      }
      const isSelected = this.state.selectedTargetId === input.id;
      const patch = {
         lifecycleBusy: false, targetEditBusy: false,
         dataTargets: this.state.dataTargets.map((item) => item.id === input.id ? target : item),
         lifecycleMessage: mode === 'delete' ? 'Target deleted. The Google Spreadsheet was not changed.' :
            mode === 'restore' ? 'Target restored.' : 'Target updated.'
      };
      if (isSelected) {
         if (mode === 'delete') {
            ++this.detailRequest;
            ++this.verifyRequest;
            Object.assign(patch, { selectedTargetId: null, targetDetail: null, targetDetailLoading: false,
               targetVerifyMessage: null, targetVerifyError: null });
         } else {
            ++this.detailRequest;
            Object.assign(patch, { targetDetail: target, targetDetailLoading: false, targetDetailError: null });
         }
         if (mode === 'restore') patch.targetFilter = 'Active';
      }
      this.setState(patch);
      DataTargetActions.fetchTargets({});
   }

   failMutation({ input, data }, mode) {
      if (!this.finishMutation(input, mode)) return;
      const code = data && (data.content || data) && (data.content || data).code;
      const message = ({ AlreadyExists: 'This spreadsheet is already registered for this company.', InvalidSpreadsheetId: 'Enter a valid Spreadsheet ID.', InvalidTargetName: 'Enter a valid Target name.', InvalidTransition: 'This Target has already changed state. Refresh and try again.', TargetArchived: 'Restore this Target before editing.', NotFound: 'Target not found.', NoChanges: 'No changes supplied.' })[code] || 'Could not save Target. Please retry.';
      this.setState({ lifecycleBusy: false, targetEditBusy: false,
         [mode === 'update' && this.state.selectedTargetId === input.id ? 'targetEditError' : 'lifecycleError']: message });
   }

   onDeleteTarget(input) { this.beginMutation(input, 'delete'); }
   onDeleteTargetCompleted(result) { this.completeMutation(result, 'delete'); }
   onDeleteTargetFailed(result) { this.failMutation(result, 'delete'); }
   onRestoreTarget(input) { this.beginMutation(input, 'restore'); }
   onRestoreTargetCompleted(result) { this.completeMutation(result, 'restore'); }
   onRestoreTargetFailed(result) { this.failMutation(result, 'restore'); }
   onUpdateTarget(input) { this.beginMutation(input, 'update'); }
   onUpdateTargetCompleted(result) { this.completeMutation(result, 'update'); }
   onUpdateTargetFailed(result) { this.failMutation(result, 'update'); }

   onClearSelectedTarget() {
      ++this.detailRequest;
      ++this.verifyRequest;
      this.setState({
         selectedTargetId: null, targetDetail: null, targetDetailLoading: false,
         targetDetailError: null, targetVerifying: false, targetVerifyMessage: null, targetVerifyError: null, targetEditError: null
      });
   }

   onGetTarget(input) {
      const { id } = input;
      if (!input.requestId) input.requestId = ++this.detailRequest;
      if (input.requestId !== this.detailRequest) return;
      ++this.verifyRequest;
      this.setState({ selectedTargetId: id, targetDetailLoading: true, targetDetailError: null, targetVerifyMessage: null, targetVerifyError: null, targetVerifying: false, targetDetail: null });
      // The API action owns the request lifecycle.
   }

   onGetTargetCompleted({ input, data }) {
      if (input.requestId !== this.detailRequest || input.id !== this.state.selectedTargetId) return;
      const result = data && data.content || {};
      this.setState({ targetDetail: result.target, targetDetailLoading: false });
   }

   onGetTargetFailed({ input, data }) {
      if (input.requestId !== this.detailRequest || input.id !== this.state.selectedTargetId) return;
      this.setState({ targetDetailLoading: false, targetDetailError: data && data.content && data.content.code === 'NotFound' ? 'Target not found.' : 'Could not load Target details. Please retry.' });
   }

   onVerifyTarget(input) {
      const { id } = input;
      if (id !== this.state.selectedTargetId) return;
      if (!input.requestId) input.requestId = ++this.verifyRequest;
      if (input.requestId !== this.verifyRequest) return;
      this.setState({ targetVerifying: true, targetVerifyMessage: null, targetVerifyError: null });
      // The API action owns the request lifecycle.
   }

   onVerifyTargetCompleted({ input, data }) {
      if (input.requestId !== this.verifyRequest || input.id !== this.state.selectedTargetId) return;
      const result = data && data.content || {};
      const message = verificationMessage(result.code, result.spreadsheetTitle);
      this.setState({ targetVerifying: false, targetVerifyMessage: result.code === 'Accessible' ? message : null, targetVerifyError: result.code === 'Accessible' ? null : message });
   }

   onVerifyTargetFailed({ input, data }) {
      if (input.requestId !== this.verifyRequest || input.id !== this.state.selectedTargetId) return;
      this.setState({ targetVerifying: false, targetVerifyError: verificationMessage(data && data.content && data.content.code) });
   }
}

export default DataTargetStore;
